import { useMemo } from 'react';

import { useTheme } from '../../theme/readTheme';
import type { ChartData, ChartFields } from '../../types';
import { Plot } from '../Plot';
import { Empty } from '../states';
import { buildFrame, frameNote, isEmpty, sum } from '../frame';
import { compact, exact, percent } from '../format';

/**
 * One number that matters.
 *
 * When the story is a single figure, a one-bar bar chart is the wrong answer —
 * the number IS the chart. This tile is the form: a label, the figure, an
 * optional change against the previous period, and a sparkline for shape.
 *
 * The figure wears ink, not a series colour. A large block of coloured text is
 * both harder to read and a misuse of the identity channel — there is only one
 * series here, so colour has nothing to identify.
 */

interface Props {
  data: ChartData;
  /** Author's column choices; absent means infer. */
  fields?: ChartFields;
}

/** Enough recent points to show shape without becoming a chart in its own right. */
const SPARK_POINTS = 12;

const Sparkline = ({ values, color, muted }: { values: number[]; color: string; muted: string }) => {
  const W = 120;
  const H = 28;
  const pad = 3;

  const max = Math.max(...values);
  const min = Math.min(...values);
  const span = max - min || 1;

  const points = values.map((v, i) => {
    const x = (i / (values.length - 1)) * (W - pad * 2) + pad;
    const y = H - pad - ((v - min) / span) * (H - pad * 2);
    return [x, y] as const;
  });

  const path = points.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const [lastX, lastY] = points[points.length - 1];

  return (
    <svg className="stat__spark" viewBox={`0 0 ${W} ${H}`} aria-hidden="true" focusable="false">
      <polyline
        points={path}
        fill="none"
        stroke={muted}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
      {/* The current period in the accent, so the eye lands on "now". */}
      <circle cx={lastX} cy={lastY} r="3.5" fill={color} />
    </svg>
  );
};

export const StatTile = ({ data, fields }: Props) => {
  const theme = useTheme();

  const view = useMemo(() => {
    const frame = buildFrame(data, fields);
    if (isEmpty(frame)) return null;

    const values = frame.series[0].values.filter((v): v is number => v !== null);
    if (!values.length) return null;

    // A temporal frame is a series of periods, so the headline is the latest
    // period and the comparison is the one before it. Anything else is a
    // breakdown, where the headline is the total.
    const periodic = frame.temporal && values.length > 1;
    const value = periodic ? values[values.length - 1] : sum(values);
    const previous = periodic ? values[values.length - 2] : null;

    const change =
      previous !== null && previous !== 0 ? (value - previous) / Math.abs(previous) : null;

    return {
      note: frameNote(frame),
      label: frame.measureName || frame.series[0].name,
      value,
      change,
      previousLabel: periodic ? frame.categories[frame.categories.length - 2] : null,
      spark: periodic ? values.slice(-SPARK_POINTS) : [],
    };
  }, [data]);

  if (!view) return <Empty />;

  const direction = view.change === null ? 0 : Math.sign(view.change);

  return (
    <Plot note={view.note}>
      <div className="stat">
      <div className="stat__label">{view.label}</div>
      {/* title carries the exact figure, so the rounded headline never hides it */}
      <div className="stat__value" title={exact(view.value)}>
        {compact(view.value)}
      </div>

      {view.change !== null && (
        <div
          className="stat__delta"
          data-direction={direction > 0 ? 'up' : direction < 0 ? 'down' : 'flat'}
        >
          <span aria-hidden="true">{direction > 0 ? '▲' : direction < 0 ? '▼' : '■'}</span>
          <span>{percent(Math.abs(view.change))}</span>
          {view.previousLabel && <span className="stat__delta-vs">vs {view.previousLabel}</span>}
        </div>
      )}

        {view.spark.length > 2 && (
          <Sparkline values={view.spark} color={theme.series[0]} muted={theme.inkMuted} />
        )}
      </div>
    </Plot>
  );
};
